/**
 * MCP Analytics Dashboard (2D.8).
 *
 * Shows AI booking metrics: tool calls, bookings, conversion rate,
 * top tools, agent types, rate limit hits.
 */
import { useCallback, useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api-client";
import { formatNumber } from "@/lib/i18n";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface McpMetrics {
  totalToolCalls: number;
  totalBookings: number;
  conversionRate: number;
  uniqueAgents: number;
  rateLimitHits: number;
  topTools: Array<{ name: string; calls: number }>;
  dailyCalls: Array<{ date: string; calls: number }>;
}

function MetricCard({ label, value, detail, trend }: {
  label: string;
  value: string;
  detail?: string;
  trend?: "up" | "down" | "flat";
}) {
  return (
    <div className="stagger-item rounded-lg border border-border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <p className="text-2xl font-bold text-foreground">{value}</p>
        {trend && (
          <span className={cn(
            "text-xs font-medium",
            trend === "up" ? "text-[var(--color-success)]" : trend === "down" ? "text-[var(--color-destructive)]" : "text-muted-foreground",
          )}>
            {trend === "up" ? "+" : trend === "down" ? "-" : ""}
          </span>
        )}
      </div>
      {detail && <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>}
    </div>
  );
}

export default function McpAnalyticsPage() {
  const [metrics, setMetrics] = useState<McpMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<"7d" | "30d" | "90d">("30d");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: McpMetrics }>(`/analytics/mcp?period=${period}`);
      setMetrics(res.data);
    } catch (caughtError) {
      setMetrics(null);
      setError(caughtError instanceof Error ? caughtError.message : "Failed to load AI booking analytics");
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">AI Booking Analytics</h2>
          <p className="text-sm text-muted-foreground">How AI agents interact with your business</p>
        </div>
        <div className="flex gap-1 rounded-lg border border-border p-1">
          {(["7d", "30d", "90d"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={cn(
                "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                period === p ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent",
              )}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          <AlertCircle className="mr-2 inline h-4 w-4" />
          {error}
          <button type="button" onClick={() => void fetchData()} className="ml-3 underline">
            Retry
          </button>
        </div>
      ) : metrics ? (
        <>
          {/* KPI cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <MetricCard label="Total Tool Calls" value={formatNumber(metrics.totalToolCalls)} />
            <MetricCard label="AI Bookings" value={String(metrics.totalBookings)} detail={`${metrics.conversionRate.toFixed(1)}% conversion`} />
            <MetricCard label="Unique AI Agents" value={String(metrics.uniqueAgents)} />
            <MetricCard label="Rate Limit Hits" value={String(metrics.rateLimitHits)} detail={metrics.rateLimitHits > 0 ? "Consider increasing limit" : "No limits hit"} />
          </div>

          {/* Top tools */}
          <div className="stagger-item rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Most Used Tools</h3>
            {metrics.topTools.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tool calls yet. Enable AI booking in settings to get started.</p>
            ) : (
              <div className="space-y-2">
                {metrics.topTools.map((tool) => {
                  const maxCalls = Math.max(...metrics.topTools.map((t) => t.calls), 1);
                  const pct = Math.round((tool.calls / maxCalls) * 100);
                  return (
                    <div key={tool.name} className="flex items-center gap-3">
                      <span className="w-32 text-sm text-foreground">{tool.name.replace(/_/g, " ")}</span>
                      <div className="flex-1 h-2 rounded-full bg-muted">
                        <div className="h-2 rounded-full bg-primary/60" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-12 text-right text-xs text-muted-foreground">{tool.calls}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* AI booking card preview */}
          <div className="stagger-item rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Your AI Booking Card</h3>
            <p className="mb-2 text-xs text-muted-foreground">
              This is how AI agents see your business. Update your AI description in Settings &gt; AI Booking.
            </p>
            <div className="rounded-md border border-dashed border-border p-4 bg-muted/30">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-bold">
                  AI
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Your Business Name</p>
                  <p className="text-xs text-muted-foreground">AI-powered services available</p>
                </div>
              </div>
              <div className="mt-2 flex gap-1">
                <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">list_services</span>
                <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">check_availability</span>
                <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">create_booking</span>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
